import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Skeleton } from '@hungryroot/pho';
import { EDIT_DELIVERY_GROCERIES_ROUTE } from '@hungryroot/routes';
import { buttonClickedEvent } from '@hungryroot/segment';
import { Button, Heading, Paragraph } from '@hungryroot/ui-kit';

import { useDietaryPreferencesUrl } from '../../../hooks/useDietaryPreferencesUrl';
import { useNextNavigation } from '../../../hooks/useNextNavigation';
import { ProductCard } from './components/ProductCard';
import { SkeletonCard } from './components/SkeletonCard';
import { useOrganicGroceries } from './hooks/useOrganicGroceries';
import { useOrganicPreferenceState } from './hooks/useOrganicPreferenceState';
import { ORGANIC_CERTIFICATION_QUERY } from './utils/sections';
import styles from './NotEnoughOrganicOptionsDeflection.module.css';

const LOADING_SKELETON_COUNT = 12;
const PAGE_POSITION = 'cancel_deflection_not_enough_organic_options';

const GROCERIES_WITH_ORGANIC_URL = `${EDIT_DELIVERY_GROCERIES_ROUTE}?${
  ORGANIC_CERTIFICATION_QUERY.key
}=${encodeURIComponent(ORGANIC_CERTIFICATION_QUERY.value)}`;

function buildCategoryUrl(categoryQueryKey: string): string {
  const params = new URLSearchParams();
  params.set(categoryQueryKey, '1');
  params.set(
    ORGANIC_CERTIFICATION_QUERY.key,
    ORGANIC_CERTIFICATION_QUERY.value
  );
  return `${EDIT_DELIVERY_GROCERIES_ROUTE}?${params.toString()}`;
}

export function NotEnoughOrganicOptionsDeflection() {
  const { cells, isLoading } = useOrganicGroceries();
  const preferenceState = useOrganicPreferenceState();
  const dietaryPreferencesUrl = useDietaryPreferencesUrl();
  const { navigateFromDeflectionContinue } = useNextNavigation();
  const history = useHistory();

  const hasNothingToShow = !isLoading && cells.length === 0;

  useEffect(() => {
    if (hasNothingToShow) {
      navigateFromDeflectionContinue({ replace: true });
    }
  }, [hasNothingToShow, navigateFromDeflectionContinue]);

  if (hasNothingToShow) {
    return null;
  }

  const isAskState = preferenceState === 'ask';

  const intro = isAskState
    ? 'We hear you. Turn on Organic Produce in your Food Profile so SmartCart™ adds organic produce to your weekly cart, and browse organic picks across other categories below.'
    : 'We hear you. Browse organic picks across produce, dairy, snacks, pantry, and more.';

  const primaryCtaText = isAskState
    ? 'Turn on Organic Produce in your Food Profile'
    : 'Browse organic groceries';
  const primaryCtaUrl = isAskState
    ? dietaryPreferencesUrl
    : GROCERIES_WITH_ORGANIC_URL;

  return (
    <div className={styles.container}>
      {isLoading ? (
        <>
          <Skeleton className={styles['skeleton-title']} />
          <Skeleton className={styles['skeleton-subcopy']} />
          <Skeleton className={styles['skeleton-subcopy']} />
          <div className={styles.grid}>
            {Array.from({ length: LOADING_SKELETON_COUNT }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </>
      ) : (
        <>
          <Heading variant="Hero" className={styles.title}>
            More organic than you might think
          </Heading>
          <Paragraph className={styles.subtitle}>{intro}</Paragraph>

          <div className={styles.grid}>
            {cells.map((cell) => (
              <ProductCard
                key={cell.key}
                product={cell.product}
                badgeLabel={cell.badgeLabel}
                count={cell.count}
                onClick={() => {
                  buttonClickedEvent({
                    button_name: `Browse organic ${cell.badgeLabel.toLowerCase()}`,
                    order_id: null,
                    page_position: PAGE_POSITION,
                  });
                  history.push(buildCategoryUrl(cell.queryKey));
                }}
              />
            ))}
          </div>

          <div className={styles['primary-cta']}>
            <Button
              onClick={() => {
                buttonClickedEvent({
                  button_name: primaryCtaText,
                  order_id: null,
                  page_position: PAGE_POSITION,
                });
                if (isAskState) {
                  // ACCOUNT_PREFERENCES_ROUTE (`/preferences/`) lives outside
                  // the SPA, so we need a full-page redirect to reach it.
                  window.location.href = primaryCtaUrl;
                } else {
                  history.push(primaryCtaUrl);
                }
              }}
              style="default"
              size="large"
              prominence="primary"
              textPrimary={primaryCtaText}
            />
          </div>
        </>
      )}
    </div>
  );
}
